import React, { useState } from "react";
import Drawer from "@mui/material/Drawer";
import { ArrowLeftIcon, TrashIcon } from "@heroicons/react/solid";
import useGet from "../../hooks/read/useGet";
import { useParams } from "react-router-dom";
import Moment from "react-moment";
import usePut from "../../hooks/usePut";
import toast from "react-hot-toast";
import PrimaryLoader from "../../components/common/PrimaryLoader";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import { Button } from "../../components/ui";
import RoundedContainer from "../../components/containers/RoundedContainer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";

const AdvanceDepartmentEditing = ({ open, onClose, mode }) => {
  const { company_employee_id, username } = useParams();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDepartmentToDelete, setSelectedDepartmentToDelete] =
    useState(null);

  const { list: departmentList } = useGet({
    url: "department_listing",
    initialData: {
      limit: 10,
      search_query: "",
      page_number: 0,
    },
  });

  const { list: userDepartmentDetails } = useGet({
    url: "user_department_details",
    initialData: {
      company_employee_id: company_employee_id,
    },
  });

  const { handleAuth: submitEmployeeDepartment } = usePut({
    url: "user_department_update",
    refreshUrl: "user_department_details",
    onSuccess: () => {
      toast.success("Employee Department Updated");
      setDepartmentState({
        department_id: "",
        company_employee_id: company_employee_id,
        effect_from: "",
      });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "There was an error while updating department, please try again later");
    },
  });

  const { handleAuth: deleteEmployeeDepartment } = usePut({
    url: "delete_user_department_map",
    refreshUrl: "user_department_details",
    onSuccess: () => {
      toast.success("Employee Department Deleted Successfully");
      setIsDeleteModalOpen(false);
      setSelectedDepartmentToDelete(null);
    },
    onError: () => {
      toast.error(
        "There was an error while deleting department, please try again later"
      );
      setIsDeleteModalOpen(false);
    },
  });

  const [departmentState, setDepartmentState] = useState({
    department_id: "",
    company_employee_id: company_employee_id,
    effect_from: "",
  });

  const handleChangeDepartment = (e) => {
    let name = e.target.name;
    let value = e.target.value;
    setDepartmentState((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleUpdateDepartment = () => {
    const { department_id, effect_from } = departmentState;
    if (!department_id || !effect_from) {
      return toast.error("Please Enter Department and Effect From");
    }
    submitEmployeeDepartment.mutate(departmentState);
  };

  const handleDeleteClick = (department) => {
    setSelectedDepartmentToDelete(department);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedDepartmentToDelete) {
      deleteEmployeeDepartment.mutate({ id: selectedDepartmentToDelete.id });
    }
  };

  const currentDepartment = userDepartmentDetails.data?.data?.[0];

  return (
    <>
      <Drawer anchor={"right"} open={open} onClose={onClose}>
        {(submitEmployeeDepartment.isLoading ||
          deleteEmployeeDepartment.isLoading) && (
          <div className=" fixed w-full h-full top-0 left-0 flex items-center justify-center bg-[#ffffffab]">
            <PrimaryLoader />
          </div>
        )}
        <div
          className="h-full bg-white p-8"
          style={{ width: "calc(100vw - 600px)" }}
        >
          <form className=" space-y-8">
            <div>
              <div className="flex items-center justify-between space-x-3 pb-5 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <ArrowLeftIcon
                    className=" text-gray-700 w-8 h-8 cursor-pointer"
                    onClick={onClose}
                  />
                  <p className="text-gray-700 text-xl font-semibold">
                    Department details for {username}
                  </p>
                </div>
                <div>
                  {mode !== "resigned" && (
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      onClick={handleUpdateDepartment}
                    >
                      Update Department
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-x-5 py-5">
                <div>
                  <div className="">
                    <label className="label">Current Department: </label>
                    <span className="block font-semibold text-base bg-red-100 text-red-600 py-2 rounded-md text-center">
                      {currentDepartment?.departmentName || "----"}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="">
                    <label className="label">New Department: </label>
                    <select
                      disabled={mode === "resigned"}
                      name="department_id"
                      value={departmentState.department_id}
                      onChange={handleChangeDepartment}
                      className="input"
                    >
                      <option value="">
                        --Select Department--
                      </option>
                      {departmentList.data?.data?.map((deptData) => {
                        return (
                          <option key={deptData.id} value={deptData.id}>
                            {deptData.departmentName}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div>
                  <div>
                    <label className="label">Effect From </label>
                    <input
                      disabled={mode === "resigned"}
                      value={departmentState.effect_from}
                      onChange={handleChangeDepartment}
                      type="date"
                      name="effect_from"
                      className="input"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <p className="text-gray-700 text-3xl font-semibold mb-4">
                  Previous Assigned Departments
                </p>
              </div>
              {userDepartmentDetails.data?.data?.length <= 0 ? (
                ""
              ) : (
                <>
                  <div className="max-w-full overflow-x-auto mt-4">
                    <Table className="table-auto w-full">
                      <TableHead>
                        <TableHeaderCell className="w-[28%] text-center">
                          Department
                        </TableHeaderCell>
                        <TableHeaderCell className="w-[30%] text-center">
                          Effected From
                        </TableHeaderCell>
                        <TableHeaderCell className="w-[30%] text-center">
                          Effected Till
                        </TableHeaderCell>
                        <TableHeaderCell className="w-[12%] text-center">
                          Actions
                        </TableHeaderCell>
                      </TableHead>
                      <TableBody>
                        {userDepartmentDetails.data?.data?.map((item) => {
                          return (
                            <TableRow key={item.id}>
                              <TableCell className="text-center text-dark font-medium text-sm">
                                {item.departmentName || "----"}
                              </TableCell>
                              <TableCell className="text-center text-dark font-medium text-sm">
                                {item.from_date ? (
                                  <Moment
                                    unix
                                    date={item.from_date}
                                    format="DD/MM/YYYY"
                                  />
                                ) : (
                                  "----"
                                )}
                              </TableCell>
                              <TableCell className="text-center text-dark font-medium text-sm">
                                {item.to_date ? (
                                  <Moment
                                    unix
                                    date={item.to_date}
                                    format="DD/MM/YYYY"
                                  />
                                ) : (
                                  "----"
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  type="button"
                                  onClick={() => handleDeleteClick(item)}
                                  variant={"ghost"}
                                  size={"icon"}
                                >
                                  <TrashIcon className="w-5 h-5 mx-auto text-gray-500 hover:text-red-600 transition-colors" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          </form>
        </div>
      </Drawer>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Department Assignment"
        description={
          selectedDepartmentToDelete ? (
            <>
              Are you sure you want to delete the{" "}
              <span className="font-bold text-red-600">
                {selectedDepartmentToDelete.departmentName}
              </span>{" "}
              department assignment? This action cannot be undone.
            </>
          ) : (
            "Are you sure you want to delete this department assignment? This action cannot be undone."
          )
        }
        confirmButtonLabel="Delete"
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default AdvanceDepartmentEditing;
